import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../../../../../components/ui/button';
import { Label } from '../../../../../../components/ui/label';
import { Input } from '../../../../../../components/ui/input';
import { Upload, FileText, X, CheckCircle, AlertTriangle, Loader2, FolderOpen } from 'lucide-react';
import weldAxiosConfig from '../../../../../../redux/WeldData/axiosConfig';
import customNotification from '../../../../../../components/ui/custom-notification';
import { logWeldLogUploaded } from '../../../../utils/productionAudit';
import { parseTlogExcelToStcRecords } from './UploadTlogExcelDataDialog';
import {
  buckerarrestorParsedFilesToTlogExcelRows,
  buildBuckerarrestorPassPreviewRows,
  type BuckerarrestorPassPreviewRow,
} from './buckerarrestorRowsToTlogExcelRows';
import { buildBuckerarrestorUpsertPreviewMetricsPayload } from './buckerarrestorPreviewMetricsPayload';
import { loadBuckerarrestorTxtFilesFromSelection, parseLoadedBuckerarrestorFiles } from './buckerarrestorLoadUploadFiles';
import type { BuckerarrestorParsedFile } from './buckerarrestorWeldingTxtParser';
import {
  buckerarrestorLoadedToUploadFiles,
  uploadBuckerarrestorTxtFilesToS3,
} from './buckerarrestorS3Upload';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../../components/ui/dialog';

function selectionLabelFromFiles(fileList: FileList | File[]): string {
  const files = Array.from(fileList || []);
  if (!files.length) return 'Selected files';
  const first = files[0] as File & { webkitRelativePath?: string };
  const relative = String(first.webkitRelativePath || '').replace(/\\/g, '/');
  if (relative.includes('/')) {
    return relative.split('/')[0] || 'Selected folder';
  }
  if (files.length === 1) return files[0].name;
  return `${files.length} files selected`;
}

/** True when SUMMARY (user) and T-log calc values are numerically different. */
function metricValuesDiffer(user: string, calc: string): boolean {
  const u = String(user || '').trim();
  const c = String(calc || '').trim();
  if (!u && !c) return false;
  if (!u || !c) return true;
  const nu = Number(u);
  const nc = Number(c);
  if (Number.isFinite(nu) && Number.isFinite(nc)) return nu !== nc;
  return u !== c;
}

/**
 * Avg: SUMMARY (left). Show T-log calc (right) only when values differ.
 * Same value → single number; differ → red user + green calc.
 */
function DualMetricCell({
  user,
  calc,
  title,
}: {
  user: string;
  calc: string;
  title?: string;
}) {
  const userText = user || '—';
  const calcText = calc || '—';
  const differ = metricValuesDiffer(user, calc);
  if (!differ) {
    return (
      <span className="whitespace-nowrap text-blueprint-blue" title={title}>
        {userText}
      </span>
    );
  }
  return (
    <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap" title={title}>
      <span className="text-readable-red">{userText}</span>
      {calc ? <span className="text-[#0B6E4F] text-[13px] font-medium">{calcText}</span> : null}
    </span>
  );
}

/** Manual upload always maps to P600Z for S/T/C + metrics APIs. */
const MANUAL_RESOLVES_TO = 'p600z';

const WELD_BULK_UPLOAD_ENDPOINT = '/log/bulkCreateUpdateLogs';
const UPSERT_PREVIEW_METRICS_ENDPOINT = '/view/upsert-preview-metrics';

interface WeldUploadPayload {
  project_id: number;
  machine_type: string;
  records: Array<Record<string, unknown>>;
}

export interface ManualWeldFolderUploadPanelProps {
  /** When false, panel state is cleared (e.g. dialog closed or machine changed away from Manual). */
  isActive: boolean;
  project?: any;
  onUploadComplete?: () => void;
  onCancel?: () => void;
}

/**
 * Manual / Buckerarrestor .txt flow — rendered inside Upload Weld Data (no separate modal).
 * S3: projects/.../manual_weld_data/{weldId}/ (no pipe / specification).
 */
export const ManualWeldFolderUploadPanel: React.FC<ManualWeldFolderUploadPanelProps> = ({
  isActive,
  project,
  onUploadComplete,
  onCancel,
}) => {
  const filesInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const [weldId, setWeldId] = useState('');
  /** User-editable job number — required; defaults to JOB-1. */
  const [jobNumber, setJobNumber] = useState('JOB-1');
  const [selectionLabel, setSelectionLabel] = useState('');
  const [fileCount, setFileCount] = useState(0);
  const [parsedFiles, setParsedFiles] = useState<BuckerarrestorParsedFile[]>([]);
  const [s3UploadFiles, setS3UploadFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processProgress, setProcessProgress] = useState<{ done: number; total: number; name: string } | null>(
    null
  );
  const [isUploading, setIsUploading] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<BuckerarrestorPassPreviewRow[]>([]);
  const [payload, setPayload] = useState<WeldUploadPayload | null>(null);
  const [rowCount, setRowCount] = useState(0);
  const [uploadChoiceOpen, setUploadChoiceOpen] = useState(false);
  /** Selected in the upload choice dialog; upload runs only on Upload click. */
  const [uploadDataMode, setUploadDataMode] = useState<'text' | 'calculated' | null>(null);

  useEffect(() => {
    if (!isActive) {
      setWeldId('');
      setJobNumber('JOB-1');
      setSelectionLabel('');
      setFileCount(0);
      setParsedFiles([]);
      setS3UploadFiles([]);
      setIsProcessing(false);
      setProcessProgress(null);
      setIsUploading(false);
      setParseError(null);
      setFileErrors([]);
      setPreviewRows([]);
      setPayload(null);
      setRowCount(0);
      setUploadChoiceOpen(false);
      setUploadDataMode(null);
      if (filesInputRef.current) filesInputRef.current.value = '';
      if (folderInputRef.current) folderInputRef.current.value = '';
    }
  }, [isActive]);

  /** Browsers need webkitdirectory on the DOM node — React props alone are unreliable. */
  useEffect(() => {
    const el = folderInputRef.current;
    if (!el) return;
    el.setAttribute('webkitdirectory', '');
    el.setAttribute('directory', '');
    el.setAttribute('mozdirectory', '');
  }, [isActive, selectionLabel]);

  const projectId = useMemo(() => {
    const value = project?.project?.projectId ?? project?.id ?? project?.projectId ?? 0;
    const n = Number(String(value).replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : 0;
  }, [project]);

  const projectName = useMemo(
    () => project?.project?.projectName ?? project?.projectName ?? project?.name ?? '',
    [project]
  );

  const rebuildPayload = (files: BuckerarrestorParsedFile[], weld: string, job: string) => {
    const trimmedWeld = weld.trim();
    const trimmedJob = job.trim();
    if (!files.length || !trimmedWeld) {
      setPayload(null);
      setPreviewRows([]);
      setRowCount(0);
      return;
    }
    // Preview can render without job; upload requires job (button disabled until entered).
    const jobForPayload = trimmedJob || '';
    const rows = buckerarrestorParsedFilesToTlogExcelRows(files, {
      projectId,
      weldId: trimmedWeld,
      jobNumber: jobForPayload,
    });
    const result = parseTlogExcelToStcRecords(rows, projectId, MANUAL_RESOLVES_TO);
    setPayload(result.payload);
    setPreviewRows(
      buildBuckerarrestorPassPreviewRows(files, { weldId: trimmedWeld, jobNumber: jobForPayload })
    );
    setRowCount(rows.length);
  };

  const clearSelection = () => {
    setSelectionLabel('');
    setFileCount(0);
    setParsedFiles([]);
    setS3UploadFiles([]);
    setParseError(null);
    setFileErrors([]);
    setPreviewRows([]);
    setPayload(null);
    setRowCount(0);
    setProcessProgress(null);
    setWeldId('');
    setJobNumber('JOB-1');
    setUploadChoiceOpen(false);
    setUploadDataMode(null);
    if (filesInputRef.current) filesInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  const processSelection = async (fileList: FileList | null, label: string) => {
    if (!fileList || fileList.length === 0) return;

    setIsProcessing(true);
    setParseError(null);
    setFileErrors([]);
    setPreviewRows([]);
    setPayload(null);
    setRowCount(0);
    setParsedFiles([]);
    setS3UploadFiles([]);
    setProcessProgress({ done: 0, total: fileList.length, name: '' });
    setSelectionLabel(label);

    try {
      const loaded = await loadBuckerarrestorTxtFilesFromSelection(fileList);
      setFileCount(loaded.files.length);
      const effectiveWeld = loaded.detectedWeldId.trim();
      setWeldId(effectiveWeld);

      if (!loaded.files.length) {
        const msg = loaded.errors.join(' ') || 'No .txt files found.';
        setParseError(msg);
        customNotification.error(msg);
        return;
      }

      setS3UploadFiles(buckerarrestorLoadedToUploadFiles(loaded.files));

      const { parsed, errors } = parseLoadedBuckerarrestorFiles(loaded.files, (done, total, name) => {
        setProcessProgress({ done, total, name });
      });
      setFileErrors([...loaded.errors, ...errors]);
      setParsedFiles(parsed);

      if (!effectiveWeld) {
        setParseError('Weld ID could not be detected from the folder. Enter Weld ID manually.');
      }
      if (!parsed.length) {
        const msg = 'No valid Buckerarrestor pass files could be parsed.';
        setParseError(msg);
        customNotification.error(msg);
        return;
      }

      if (effectiveWeld) {
        rebuildPayload(parsed, effectiveWeld, jobNumber);
        customNotification.success(
          `Processed ${parsed.length} file(s) → ${parsed.reduce((n, f) => n + f.sampleRows.length, 0)} sample row(s)`
        );
      }
    } catch (error: any) {
      const msg = error?.message || 'Failed to process Buckerarrestor files';
      setParseError(msg);
      customNotification.error(msg);
    } finally {
      setIsProcessing(false);
      setProcessProgress(null);
    }
  };

  const handleWeldIdChange = (value: string) => {
    setWeldId(value);
    if (parsedFiles.length && value.trim()) {
      setParseError(null);
      rebuildPayload(parsedFiles, value, jobNumber);
    } else if (!value.trim()) {
      setPayload(null);
      setPreviewRows([]);
    }
  };

  const handleJobNumberChange = (value: string) => {
    setJobNumber(value);
    if (parsedFiles.length && weldId.trim()) {
      rebuildPayload(parsedFiles, weldId, value);
    }
  };

  const validateUploadReady = (): boolean => {
    if (!payload || payload.records.length === 0) {
      customNotification.error('No payload generated. Choose a valid weld folder first.');
      return false;
    }
    if (!weldId.trim()) {
      customNotification.error('Weld ID is required.');
      return false;
    }
    if (!jobNumber.trim()) {
      customNotification.error('Job number is required.');
      return false;
    }
    if (!projectId) {
      customNotification.error('Project ID is missing. Select a project and try again.');
      return false;
    }
    if (!s3UploadFiles.length) {
      customNotification.error('No files available for S3 upload. Re-select the folder and try again.');
      return false;
    }
    return true;
  };

  const requestUpload = () => {
    if (!validateUploadReady()) return;
    setUploadDataMode('text');
    setUploadChoiceOpen(true);
  };

  /** @param useCalculatedData true = server status calc from samples; false = txt SUMMARY metrics */
  const handleUpload = async (useCalculatedData: boolean) => {
    if (!validateUploadReady() || !payload) return;

    setUploadChoiceOpen(false);
    setIsUploading(true);
    try {
      const response = await weldAxiosConfig.post(WELD_BULK_UPLOAD_ENDPOINT, {
        ...payload,
        trigger_status_calculation: useCalculatedData,
        replace_existing_weld_views: true,
        lead_torch_only: true,
        is_fileUpload: true,
      });
      if (response.status < 200 || response.status >= 300) {
        throw new Error('Failed to upload weld S/T/C records');
      }

      if (!useCalculatedData) {
        if (!previewRows.length) {
          throw new Error('No preview metrics to upsert. Re-process the folder and try again.');
        }
        const metricsPayload = buildBuckerarrestorUpsertPreviewMetricsPayload(previewRows, {
          projectId,
          machineType: payload.machine_type,
          weldId: weldId.trim(),
          jobNumber: jobNumber.trim(),
        });
        const metricsRes = await weldAxiosConfig.post(
          UPSERT_PREVIEW_METRICS_ENDPOINT,
          metricsPayload
        );
        if (metricsRes.status < 200 || metricsRes.status >= 300) {
          throw new Error('S/T/C saved but pass/zone/tilt preview metrics upsert failed');
        }
      }

      const s3Uploaded = await uploadBuckerarrestorTxtFilesToS3({
        files: s3UploadFiles,
        projectId,
        weldId: weldId.trim(),
      });
      if (!s3Uploaded.length) {
        throw new Error('Weld data saved, but S3 file batch upload returned no files');
      }

      const weldNumbers = Array.from(
        new Set(
          payload.records
            .map((record) => String(record.Weld_number ?? record.weld_number ?? '').trim())
            .filter(Boolean)
        )
      );
      const jobNumbers = Array.from(
        new Set(
          payload.records
            .map((record) => String(record.Job_number ?? record.jobNumber ?? '').trim())
            .filter(Boolean)
        )
      );

      logWeldLogUploaded({
        projectId,
        projectName,
        fileName: selectionLabel || `buckerarrestor-txt (${fileCount} files)`,
        machineType: payload.machine_type,
        recordCount: payload.records.length,
        tRecordCount: payload.records.filter((record) => record.Record === 'T').length,
        sRecordCount: payload.records.filter((record) => record.Record === 'S').length,
        cRecordCount: payload.records.filter((record) => record.Record === 'C').length,
        weldNumbers,
        jobNumbers,
      });

      customNotification.success(
        useCalculatedData
          ? `Injected ${rowCount} sample row(s) as S/T/C, then batch-uploaded ${s3Uploaded.length} file(s) to S3`
          : `Injected preview metrics, then batch-uploaded ${s3Uploaded.length} file(s) to S3 (manual_weld_data/${weldId.trim()})`
      );
      onUploadComplete?.();
    } catch (error: any) {
      customNotification.error(
        error?.response?.data?.message || error?.message || 'Failed to upload Manual weld data.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const sCount = payload?.records.filter((r) => r.Record === 'S').length ?? 0;
  const tCount = payload?.records.filter((r) => r.Record === 'T').length ?? 0;
  const cCount = payload?.records.filter((r) => r.Record === 'C').length ?? 0;
  const canChooseFiles = !isProcessing && !isUploading;

  if (!isActive) return null;

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-hero-red text-white rounded-full flex items-center justify-center text-[12px] font-medium">
            2
          </div>
          <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">
            Choose files or folder
          </h3>
        </div>

        <p className="text-[13px] text-text-grey">
          Pick individual .txt files, or a whole weld folder (all .txt files inside).
        </p>

        {selectionLabel ? (
          <div className="flex items-center p-3 bg-page-bg border border-[#E2E5EA] rounded-lg">
            <FileText className="h-4 w-4 text-heritage-grey mr-2 flex-shrink-0" />
            <span className="text-[13px] text-text-grey flex-1 truncate">
              {selectionLabel}
              {fileCount > 0 ? ` (${fileCount} .txt)` : ''}
            </span>
            {isProcessing && (
              <span className="text-[12px] text-hero-red mr-2 flex items-center gap-1">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Processing...
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              disabled={isUploading || isProcessing}
              className="h-6 w-6 p-0 hover:bg-hero-red-10 hover:text-readable-red"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className={!canChooseFiles ? 'opacity-60' : ''}>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                size="lg"
                disabled={!canChooseFiles}
                onClick={() => filesInputRef.current?.click()}
                className="flex items-center gap-2 w-full justify-center"
              >
                <Upload className="h-4 w-4" />
                Choose files
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                disabled={!canChooseFiles}
                onClick={() => folderInputRef.current?.click()}
                className="flex items-center gap-2 w-full justify-center"
              >
                <FolderOpen className="h-4 w-4" />
                Select folder
              </Button>
            </div>
            <input
              ref={filesInputRef}
              type="file"
              accept=".txt,text/plain"
              className="hidden"
              multiple
              disabled={!canChooseFiles}
              aria-label="Choose files"
              onChange={(e) => {
                const list = e.target.files;
                if (!list?.length) return;
                void processSelection(list, selectionLabelFromFiles(list));
                e.target.value = '';
              }}
            />
            <input
              ref={folderInputRef}
              type="file"
              className="hidden"
              multiple
              disabled={!canChooseFiles}
              aria-label="Select folder"
              onChange={(e) => {
                const list = e.target.files;
                if (!list?.length) return;
                void processSelection(list, selectionLabelFromFiles(list));
                e.target.value = '';
              }}
            />
          </div>
        )}

        {(selectionLabel || isProcessing) && (
          <div className="flex flex-wrap gap-4 max-w-xl">
            <div className="flex-1 min-w-[160px] space-y-1.5">
              <Label htmlFor="manual-weld-id" className="text-[12px] font-medium text-text-grey">
                Weld ID <span className="text-readable-red">*</span>
              </Label>
              <Input
                id="manual-weld-id"
                value={weldId}
                onChange={(e) => handleWeldIdChange(e.target.value)}
                placeholder="From folder name (e.g. WPS-05-02)"
                disabled={isUploading}
                className="h-9 text-[13px]"
              />
            </div>
            <div className="flex-1 min-w-[160px] space-y-1.5">
              <Label htmlFor="manual-job-number" className="text-[12px] font-medium text-text-grey">
                Job number <span className="text-readable-red">*</span>
              </Label>
              <Input
                id="manual-job-number"
                value={jobNumber}
                onChange={(e) => handleJobNumberChange(e.target.value)}
                placeholder="JOB-1"
                disabled={isUploading}
                className="h-9 text-[13px]"
              />
            </div>
          </div>
        )}

        {isProcessing && (
          <div className="flex items-start gap-3 p-3 bg-page-bg border border-[#E2E5EA] rounded-lg text-[13px] text-text-grey">
            <Loader2 className="h-4 w-4 mt-0.5 flex-shrink-0 animate-spin text-hero-red" />
            <span>
              Processing files…
              {processProgress
                ? ` ${processProgress.done} / ${processProgress.total}${
                    processProgress.name ? ` — ${processProgress.name}` : ''
                  }`
                : ''}
            </span>
          </div>
        )}

        {parseError && (
          <div className="flex items-start gap-3 p-3 bg-[rgba(196,0,31,0.06)] border border-hero-red-20 rounded-lg text-[13px] text-readable-red">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-readable-red" />
            <span>{parseError}</span>
          </div>
        )}

        {fileErrors.length > 0 && (
          <div className="p-3 bg-[rgba(196,0,31,0.04)] border border-hero-red-20 rounded-lg text-[12px] text-readable-red max-h-[120px] overflow-auto">
            {fileErrors.map((err) => (
              <div key={err}>{err}</div>
            ))}
          </div>
        )}

        {payload && !parseError && !isProcessing && (
          <div className="flex items-start gap-3 p-3 bg-[rgba(31,154,58,0.08)] border border-[rgba(31,154,58,0.25)] rounded-lg text-[13px] text-galvanizing-green">
            <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              {fileCount} file(s), {rowCount} sample row(s) → {sCount} S + {tCount} T + {cCount} C
              (one S/C per pass).
            </span>
          </div>
        )}
      </div>

      {previewRows.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-[13px] font-semibold text-blueprint-blue">
            Preview (one row per pass — Volts/Current Min·Avg·Max)
          </h4>
          <div className="border border-[#E2E5EA] rounded-lg overflow-auto max-h-[320px]">
            <table className="w-full text-[12px]">
              <thead className="bg-page-bg sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2" rowSpan={2}>
                    Weld
                  </th>
                  <th className="text-left px-3 py-2" rowSpan={2}>
                    Job
                  </th>
                  <th className="text-left px-3 py-2" rowSpan={2}>
                    Pass
                  </th>
                  <th className="text-center px-3 py-2 border-l border-[#E2E5EA]" colSpan={3}>
                    Volts (V)
                  </th>
                  <th className="text-center px-3 py-2 border-l border-[#E2E5EA]" colSpan={3}>
                    Current (A)
                  </th>
                  <th className="text-center px-3 py-2 border-l border-[#E2E5EA] whitespace-nowrap" rowSpan={2}>
                    Travel (in/min)
                  </th>
                  <th className="text-center px-3 py-2 border-l border-[#E2E5EA] whitespace-nowrap" rowSpan={2}>
                    Heat (KJ/in)
                  </th>
                </tr>
                <tr>
                  <th className="text-left px-3 py-1 border-l border-[#E2E5EA] font-medium text-heritage-grey">
                    Min
                  </th>
                  <th className="text-left px-3 py-1 font-medium text-heritage-grey">Avg</th>
                  <th className="text-left px-3 py-1 font-medium text-heritage-grey">Max</th>
                  <th className="text-left px-3 py-1 border-l border-[#E2E5EA] font-medium text-heritage-grey">
                    Min
                  </th>
                  <th className="text-left px-3 py-1 font-medium text-heritage-grey">Avg</th>
                  <th className="text-left px-3 py-1 font-medium text-heritage-grey">Max</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, idx) => (
                  <tr key={`${row.passName}-${idx}`} className="border-t border-[#EEF0F3]">
                    <td className="px-3 py-2">{row.weldNumber || '—'}</td>
                    <td className="px-3 py-2">{row.jobNumber || '—'}</td>
                    <td className="px-3 py-2">{row.passName || '—'}</td>
                    <td className="px-3 py-2 border-l border-[#EEF0F3]">{row.voltsMin || '—'}</td>
                    <td className="px-3 py-2">
                      <DualMetricCell
                        user={row.voltsAvg}
                        calc={row.voltsAvgCalc}
                        title="User SUMMARY avg · T-log sample avg"
                      />
                    </td>
                    <td className="px-3 py-2">{row.voltsMax || '—'}</td>
                    <td className="px-3 py-2 border-l border-[#EEF0F3]">{row.currentMin || '—'}</td>
                    <td className="px-3 py-2">
                      <DualMetricCell
                        user={row.currentAvg}
                        calc={row.currentAvgCalc}
                        title="User SUMMARY avg · T-log sample avg"
                      />
                    </td>
                    <td className="px-3 py-2">{row.currentMax || '—'}</td>
                    <td className="px-3 py-2 border-l border-[#EEF0F3] text-center">
                      {row.travelAvg || row.travelMin || row.travelMax || '—'}
                    </td>
                    <td className="px-3 py-2 border-l border-[#EEF0F3] text-center">
                      {row.heatInput || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button
          variant="outline"
          onClick={() => {
            if (isUploading) return;
            clearSelection();
            onCancel?.();
          }}
          disabled={isUploading}
          className="min-w-[100px]"
        >
          Cancel
        </Button>
        <Button
          onClick={requestUpload}
          disabled={
            !payload ||
            isUploading ||
            isProcessing ||
            Boolean(parseError) ||
            !weldId.trim() ||
            !jobNumber.trim()
          }
          className="bg-hero-red hover:bg-hero-red-60 text-white min-w-[120px]"
          loading={isUploading}
          loadingText="Uploading..."
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload Data
        </Button>
      </div>

      <Dialog
        open={uploadChoiceOpen}
        onOpenChange={(open) => {
          if (isUploading) return;
          setUploadChoiceOpen(open);
          if (!open) setUploadDataMode(null);
        }}
      >
        <DialogContent className="z-[60] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-[18px] font-semibold text-blueprint-blue">
              Which data do you want to upload?
            </DialogTitle>
            <DialogDescription className="text-[13px] text-text-grey leading-5 pt-1">
              Choose how pass, zone, and tilt Min/Avg/Max are applied for this weld, then click
              Upload.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 py-1" role="radiogroup" aria-label="Upload data source">
            <button
              type="button"
              role="radio"
              aria-checked={uploadDataMode === 'text'}
              disabled={isUploading}
              onClick={() => setUploadDataMode('text')}
              className={`h-auto min-h-[44px] w-full rounded-lg border px-4 py-3 text-left transition-colors ${
                uploadDataMode === 'text'
                  ? 'border-hero-red bg-hero-red-10'
                  : 'border-[#E2E5EA] bg-white hover:border-heritage-grey'
              }`}
            >
              <span className="flex flex-col items-start gap-0.5">
                <span className="text-[13px] font-semibold text-blueprint-blue">Text file data</span>
                <span className="text-[12px] font-normal text-heritage-grey">
                  Use Min/Avg/Max from the welding txt SUMMARY
                </span>
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={uploadDataMode === 'calculated'}
              disabled={isUploading}
              onClick={() => setUploadDataMode('calculated')}
              className={`h-auto min-h-[44px] w-full rounded-lg border px-4 py-3 text-left transition-colors ${
                uploadDataMode === 'calculated'
                  ? 'border-hero-red bg-hero-red-10'
                  : 'border-[#E2E5EA] bg-white hover:border-heritage-grey'
              }`}
            >
              <span className="flex flex-col items-start gap-0.5">
                <span className="text-[13px] font-semibold text-blueprint-blue">Calculated data</span>
                <span className="text-[12px] font-normal text-heritage-grey">
                  Recalculate from sample rows on the server
                </span>
              </span>
            </button>
          </div>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              disabled={isUploading}
              onClick={() => {
                setUploadChoiceOpen(false);
                setUploadDataMode(null);
              }}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              disabled={isUploading || uploadDataMode == null}
              onClick={() => void handleUpload(uploadDataMode === 'calculated')}
              className="bg-hero-red hover:bg-hero-red-60 text-white min-w-[100px]"
              loading={isUploading}
              loadingText="Uploading..."
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
